import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { API, useAuth } from '@/App';
import axios from 'axios';

// localStorage cache so the next page load renders any platform-gated UI
// (e.g. sidebar tabs that depend on `credit_engine`, `newsboard`,
// `pool_draw`…) instantly instead of flashing or staying empty for a
// few seconds while /api/feature-flags/active resolves. We still revalidate
// in the background. Feb 2026 — "empty sidebar for 5s" fix.
const CACHE_KEY_PREFIX = 'unexa:platform_flags:';
const CACHE_VERSION = 'v1';

function cacheKey(country) {
  return `${CACHE_KEY_PREFIX}${(country || 'auto').toLowerCase()}`;
}

function readCache(country) {
  try {
    const raw = localStorage.getItem(cacheKey(country));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== CACHE_VERSION) return null;
    return parsed.data || null;
  } catch {
    return null;
  }
}

function writeCache(country, flags) {
  try {
    localStorage.setItem(
      cacheKey(country),
      JSON.stringify({ v: CACHE_VERSION, ts: Date.now(), data: flags })
    );
  } catch {
    /* ignore */
  }
}

const PlatformFlagsCtx = createContext({ isEnabled: () => true, loaded: false });

export function PlatformFlagsProvider({ children }) {
  const { user } = useAuth();
  // Seed synchronously from cache. The 'auto' key is hit on first paint
  // before we know the user's country — if the user later flips to a
  // different country we re-fetch with the country param and overwrite.
  const initial = (() => {
    const userCountry = (user?.current_country || user?.country || '').toLowerCase().trim();
    const c = readCache(userCountry || 'auto');
    return { flags: c || {}, loaded: !!c };
  })();
  const [flags, setFlags] = useState(initial.flags);
  const [loaded, setLoaded] = useState(initial.loaded);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const userCountry = (user?.current_country || user?.country || '').toLowerCase().trim();
    // Cache hydration already happened in the useState initializer.
    // If user country changes mid-session, this effect re-fires and the
    // API response below updates state + cache for the new country.
    const params = userCountry ? { country: userCountry } : {};
    axios.get(`${API}/feature-flags/active`, { params })
      .then(res => {
        if (cancelled) return;
        const data = res.data || {};
        setFlags(data);
        setLoadFailed(false);
        writeCache(userCountry || 'auto', data);
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [user?.current_country, user?.country]);

  const isEnabled = useCallback((key) => {
    // While flags are still loading, default to ENABLED so tabs/sections
    // don't briefly disappear then pop in. Once loaded we use the real value.
    if (!loaded) return true;
    // If load failed, show everything as fallback (better UX than hiding all)
    if (loadFailed) return true;
    // Check the actual flag value — if key doesn't exist in response, default to true
    return flags[key] !== false;
  }, [loaded, loadFailed, flags]);

  return (
    <PlatformFlagsCtx.Provider value={{ flags, isEnabled, loaded }}>
      {children}
    </PlatformFlagsCtx.Provider>
  );
}

export function usePlatformFlags() {
  return useContext(PlatformFlagsCtx);
}
